/**
 * Pure tree-selection helpers backing TreeViewControl's "select branch" affordance.
 *
 * Kept out of the component so the branch semantics can be unit-tested without mounting Vuetify,
 * and so the O(n) guarantees below stay explicit.
 */

/**
 * Indexes a nested tree in DFS pre-order with interval labeling.
 *
 * The point of the interval labeling is that a node's subtree is a CONTIGUOUS slice of `order`:
 * `order.slice(...range.get(id))` is exactly "the node plus all its descendants", at any depth.
 * That makes both the branch toggle and the full-branch derivation linear without ever storing a
 * per-node descendant array (which would be O(n·depth) memory on a deep organisation tree).
 *
 * @param {Array<object>} nodes nested nodes, each `{ id, children? }`
 * @returns {{ order: number[], range: Map<number, [number, number]>, childIds: Map<number, number[]>, parentOf: Map<number, number>, nodeById: Map<number, object>, branchIds: Set<number> }}
 */
export function buildTreeIndex(nodes) {
	const order = [];
	const range = new Map();
	const childIds = new Map();
	const parentOf = new Map();
	const nodeById = new Map();
	const branchIds = new Set();

	const walk = (currentNodes, parentId) => {
		for (const node of currentNodes || []) {
			if (!node || node.id === undefined || node.id === null) continue;
			const start = order.length;
			order.push(node.id);
			nodeById.set(node.id, node);
			if (parentId !== undefined) parentOf.set(node.id, parentId);

			const kids = Array.isArray(node.children) ? node.children : [];
			childIds.set(
				node.id,
				kids.map((child) => child.id)
			);
			if (kids.length > 0) {
				branchIds.add(node.id);
				walk(kids, node.id);
			}
			range.set(node.id, [start, order.length]);
		}
	};

	walk(nodes, undefined);
	return { order, range, childIds, parentOf, nodeById, branchIds };
}

/**
 * Ids whose whole branch is selected: the node itself AND every descendant (spec RG-17/RG-21).
 *
 * Derived, never stored — which is what makes RG-18 fall out for free: unchecking anything inside a
 * branch simply drops it (and its ancestors) from this set on the next recompute, without touching
 * any other selection.
 *
 * One reverse pass over the pre-order is enough: in pre-order every descendant of index i sits at
 * i+1…end-1, so walking backwards always visits children before their parent.
 *
 * @param {{ order: number[], childIds: Map<number, number[]> }} treeIndex from buildTreeIndex()
 * @param {Set<number>} selectedIds
 * @returns {Set<number>}
 */
export function computeFullBranchIds(treeIndex, selectedIds) {
	const { order, childIds } = treeIndex;
	const full = new Set();
	for (let i = order.length - 1; i >= 0; i--) {
		const id = order[i];
		if (!selectedIds.has(id)) continue;
		const kids = childIds.get(id) || [];
		if (kids.length === 0 || kids.every((childId) => full.has(childId))) full.add(id);
	}
	return full;
}

/**
 * Toggles a whole branch (spec RG-15/RG-16): fills it, or empties it when it is already full.
 *
 * Returns a NEW selection array in stable tree order, deduplicated — which also keeps
 * getFullValues() deterministic downstream.
 *
 * @param {{ order: number[], range: Map<number, [number, number]> }} treeIndex
 * @param {Array<number>} selectedIds current selection
 * @param {number} nodeId branch root
 * @param {boolean} isFull whether that branch is currently full (from computeFullBranchIds)
 * @returns {Array<number>}
 */
export function toggleBranchSelection(treeIndex, selectedIds, nodeId, isFull) {
	const { order, range } = treeIndex;
	const bounds = range.get(nodeId);
	if (!bounds) return [...selectedIds];

	const next = new Set(selectedIds);
	for (let i = bounds[0]; i < bounds[1]; i++) {
		if (isFull) next.delete(order[i]);
		else next.add(order[i]);
	}
	return order.filter((id) => next.has(id));
}

/**
 * Branch nodes that must be expanded so every search match is actually visible (spec RG-13).
 *
 * Vuetify's own `:search` keeps ancestors of matches in `visibleIds`, but only unhides them with a
 * CSS class — a match nested under a collapsed group stays invisible. So the ancestors have to be
 * pushed into the controlled `opened` model as well.
 *
 * @param {{ order: number[], nodeById: Map<number, object>, parentOf: Map<number, number>, branchIds: Set<number> }} treeIndex
 * @param {(node: object) => boolean} matches predicate, the SAME one handed to `:custom-filter`
 * @returns {Array<number>}
 */
export function collectSearchOpenedIds(treeIndex, matches) {
	const { order, nodeById, parentOf, branchIds } = treeIndex;
	const opened = new Set();
	const walked = new Set();

	for (const id of order) {
		if (!matches(nodeById.get(id))) continue;
		if (branchIds.has(id)) opened.add(id);

		// `walked` must be separate from `opened`: memoizing on `opened` would stop the upward walk
		// at an ancestor that happened to be a match itself but whose own ancestors are not in yet.
		let parentId = parentOf.get(id);
		while (parentId !== undefined && !walked.has(parentId)) {
			walked.add(parentId);
			opened.add(parentId);
			parentId = parentOf.get(parentId);
		}
	}

	return [...opened];
}

/**
 * Case/accent-insensitive "does this node match the query" over the given fields.
 *
 * @param {object} node
 * @param {string} query already lower-cased and trimmed
 * @param {Array<string>} fields
 * @returns {boolean}
 */
export function nodeMatchesQuery(node, query, fields) {
	if (!query) return true;
	if (!node) return false;
	return (fields || ['title']).some((key) =>
		String(node[key] ?? '')
			.toLocaleLowerCase()
			.includes(query)
	);
}
